import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Spinner,
  Banner,
  Button,
  Tabs,
  Modal,
  FormLayout,
  TextField,
  Select,
  DataTable,
  EmptyState,
  Icon,
  ProgressBar,
  Grid,
  Checkbox,
  IndexTable,
  useIndexResourceState,
  Filters,
  ChoiceList,
  RangeSlider,
  Divider,
  DatePicker,
  Tooltip
} from '@shopify/polaris';
import {
  StoreIcon,
  CheckCircleIcon,
  PlusIcon,
  RefreshIcon,
  CashDollarIcon,
  OrderIcon,
  ReceiptRefundIcon,
  ReturnIcon
} from '@shopify/polaris-icons';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../utils/api';
import { useBrandAnalytics, useBrandOrders, useMarketplacesList } from '../utils/queries';
import MetricCard, { RevenueCard, CommissionCard, OrdersCard } from '../components/Analytics/MetricCard';
import MarketplaceBreakdown from '../components/Analytics/MarketplaceBreakdown';
import { validateNumeric } from '../utils/inputSecurity';
import ShippingPricingTable from '../components/ShippingPricingTable';
import CommissionAdjustmentsTable from '../components/CommissionAdjustments/CommissionAdjustmentsTable';
import { CommissionSettings } from '../components/CommissionSettings';
import { InvoiceSettings } from '../components/InvoiceSettings';

export default function BrandConfiguration() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper function to format activity details with robust JSON parsing
  const formatActivityDetails = (action, details) => {
    // Safely parse details if it's a JSON string
    let parsedDetails = details;
    if (typeof details === 'string' && details.trim()) {
      try {
        parsedDetails = JSON.parse(details);
      } catch (parseError) {
        // Only log parsing warnings in development environment
        if (process.env.NODE_ENV === 'development') {
          console.warn('BrandConfiguration: Failed to parse activity details JSON:', {
            action,
            details,
            error: parseError.message
          });
        }
        return details; // Return original string if parsing fails
      }
    }

    // Format based on action type
    switch (action.toLowerCase()) {
      case 'updated default commission rate':
      case 'commission_rate_updated':
        return `Changed from ${(parsedDetails.oldRate * 100).toFixed(1)}% to ${(parsedDetails.newRate * 100).toFixed(1)}%`;

      case 'added commission override':
      case 'commission_override_added':
        return `${parsedDetails.orderTag}: ${(parsedDetails.commissionRate * 100).toFixed(1)}%`;

      case 'removed commission override':
      case 'commission_override_deleted':
        return `Removed override for ${parsedDetails.orderTag}`;

      case 'added shipping fee rule':
      case 'shipping_fee_added':
        return `${parsedDetails.shippingMethod} (${parsedDetails.weightMin}-${parsedDetails.weightMax} lbs): $${parsedDetails.fee.toFixed(2)}`;

      case 'removed shipping fee rule':
      case 'shipping_fee_deleted':
        return `Removed ${parsedDetails.shippingMethod} fee rule`;

      case 'shipping_fees_reset_to_default':
        return `Reset ${parsedDetails.deletedCount || 0} shipping fee override(s) to global defaults`;

      case 'activated brand':
      case 'brand_activated':
        return `Brand status changed from ${parsedDetails.previousStatus ? 'Active' : 'Inactive'} to ${parsedDetails.newStatus ? 'Active' : 'Inactive'}`;

      case 'deactivated brand':
      case 'brand_deactivated':
        return `Brand status changed from ${parsedDetails.previousStatus ? 'Active' : 'Inactive'} to ${parsedDetails.newStatus ? 'Active' : 'Inactive'}`;

      case 'shipping_pricing_updated':
      case 'bulk_shipping_pricing_updated':
        if (parsedDetails.changes && Array.isArray(parsedDetails.changes)) {
          return parsedDetails.changes.map(change => {
            const oldPrice = change.oldFee != null ? `$${Number(change.oldFee).toFixed(2)}` : '—';
            const newPrice = change.newFee != null ? `$${Number(change.newFee).toFixed(2)}` : '—';
            return `${change.service} ${change.weightRange}: ${oldPrice} → ${newPrice}`;
          }).join('; ');
        }
        // Fallback for updates without detailed changes
        const summary = [];
        if (parsedDetails.updated > 0) summary.push(`${parsedDetails.updated} updated`);
        if (parsedDetails.created > 0) summary.push(`${parsedDetails.created} created`);
        if (parsedDetails.deleted > 0) summary.push(`${parsedDetails.deleted} deleted`);
        return summary.join(', ') || 'No changes';

      default:
        // For unknown actions, try to show meaningful info
        if (parsedDetails && typeof parsedDetails === 'object') {
          if (parsedDetails.previousStatus !== undefined && parsedDetails.newStatus !== undefined) {
            return `Status changed from ${parsedDetails.previousStatus ? 'Active' : 'Inactive'} to ${parsedDetails.newStatus ? 'Active' : 'Inactive'}`;
          }
          // Show key-value pairs for other objects
          return Object.entries(parsedDetails)
            .filter(([key, value]) => key !== 'adminUserEmail') // Hide admin email from details
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
        }
        return details; // Return original if can't format
    }
  };

  // Core state
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Initialize tab based on URL parameters
  const getInitialTab = () => {
    const tab = searchParams.get('tab');
    switch (tab) {
      case 'settings': return 0;
      case 'shipping': return 1;
      case 'sync': return 2;
      case 'analytics': return 3;
      case 'orders': return 4;
      case 'adjustments': return 5;
      case 'activity': return 6;
      default: return 0;
    }
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());
  const [analyticsFilter, setAnalyticsFilter] = useState('30'); // days

  // Analytics date range state
  const [dateRangeMode, setDateRangeMode] = useState('preset'); // 'preset' | 'custom' | 'lastMonth' | 'thisMonth'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);

  // DatePicker state - uses Date objects and {month, year} format
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(),
    end: new Date()
  });
  const [datePickerMonth, setDatePickerMonth] = useState(new Date().getMonth());
  const [datePickerYear, setDatePickerYear] = useState(new Date().getFullYear());

  // Commission state
  const [defaultRate, setDefaultRate] = useState('30');



  // Return fees state
  const [defaultReturnFee, setDefaultReturnFee] = useState('0.00');
  const [hasDefaultReturnFee, setHasDefaultReturnFee] = useState(false);
  const [marketplaceReturnFees, setMarketplaceReturnFees] = useState({});
  const [showReturnFeeModal, setShowReturnFeeModal] = useState(false);
  const [editingMarketplace, setEditingMarketplace] = useState('');
  const [newReturnFee, setNewReturnFee] = useState('0.00');
  // Global return fee defaults and override tracking
  const [globalReturnFeeDefaults, setGlobalReturnFeeDefaults] = useState({ default: 0, hasGlobalDefault: false, marketplaces: {} });
  const [returnFeeOverrideKeys, setReturnFeeOverrideKeys] = useState([]);
  // Reset to default modals
  const [showResetDefaultFeeModal, setShowResetDefaultFeeModal] = useState(false);
  const [showResetMarketplaceFeesModal, setShowResetMarketplaceFeesModal] = useState(false);
  const [resettingFees, setResettingFees] = useState(false);

  // Shipping enable/disable state
  const [shippingEnabled, setShippingEnabled] = useState(true);

  const [newRecipientEmail, setNewRecipientEmail] = useState('');

  // Orders state management (based on OrderManagement.jsx)
  const [ordersSearchQuery, setOrdersSearchQuery] = useState('');
  const [debouncedOrdersSearch, setDebouncedOrdersSearch] = useState('');
  const [ordersSortBy, setOrdersSortBy] = useState('orderDate');
  const [ordersSortDirection, setOrdersSortDirection] = useState('descending');
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);

  // Resync state
  const [resyncingOrderId, setResyncingOrderId] = useState(null);
  const [resyncBanner, setResyncBanner] = useState(null);
  const resyncBannerTimeoutRef = useRef(null);

  // Filter states
  const [ordersStatusFilter, setOrdersStatusFilter] = useState([]);
  const [ordersMarketplaceFilter, setOrdersMarketplaceFilter] = useState([]);
  const [ordersAmountRange, setOrdersAmountRange] = useState([0, 5000]);

  // Date range filter states
  const [ordersStartDate, setOrdersStartDate] = useState('');
  const [ordersEndDate, setOrdersEndDate] = useState('');
  const ordersItemsPerPage = 10;

  // Helper function to format date as YYYY-MM-DD without timezone shift
  // This avoids the bug where toISOString() converts to UTC, shifting dates by 1 day
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Date calculation helper functions
  const getLastMonthDates = useCallback(() => {
    const today = new Date();
    const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return {
      startDate: formatDateLocal(firstDayLastMonth),
      endDate: formatDateLocal(lastDayLastMonth)
    };
  }, []);

  const getThisMonthToDateDates = useCallback(() => {
    const today = new Date();
    const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      startDate: formatDateLocal(firstDayThisMonth),
      endDate: formatDateLocal(today)
    };
  }, []);

  // Get analytics parameters based on date range mode
  const getAnalyticsParams = useCallback(() => {
    switch (dateRangeMode) {
      case 'lastMonth': {
        const { startDate, endDate } = getLastMonthDates();
        return { startDate, endDate };
      }
      case 'thisMonth': {
        const { startDate, endDate } = getThisMonthToDateDates();
        return { startDate, endDate };
      }
      case 'custom':
        if (customStartDate && customEndDate) {
          return { startDate: customStartDate, endDate: customEndDate };
        }
        return { days: parseInt(analyticsFilter) };
      case 'preset':
      default:
        return { days: parseInt(analyticsFilter) };
    }
  }, [dateRangeMode, analyticsFilter, customStartDate, customEndDate, getLastMonthDates, getThisMonthToDateDates]);

  // Debounce search query for orders
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOrdersSearch(ordersSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [ordersSearchQuery]);

  // Cleanup resync banner timeout on unmount
  useEffect(() => {
    return () => {
      if (resyncBannerTimeoutRef.current) {
        clearTimeout(resyncBannerTimeoutRef.current);
      }
    };
  }, []);

  // Build query parameters for orders API
  const ordersQueryParams = useMemo(() => ({
    page: ordersCurrentPage,
    limit: ordersItemsPerPage,
    sortBy: ordersSortBy,
    sortDirection: ordersSortDirection,
    search: debouncedOrdersSearch || undefined,
    statusFilter: ordersStatusFilter.length > 0 ? ordersStatusFilter : undefined,
    marketplace: ordersMarketplaceFilter.length > 0 ? ordersMarketplaceFilter : undefined,
    amountMin: ordersAmountRange[0] > 0 ? ordersAmountRange[0] : undefined,
    amountMax: ordersAmountRange[1] < 5000 ? ordersAmountRange[1] : undefined,
    startDate: ordersStartDate || undefined,
    endDate: ordersEndDate || undefined,
  }), [ordersCurrentPage, ordersSortBy, ordersSortDirection, debouncedOrdersSearch, ordersStatusFilter, ordersMarketplaceFilter, ordersAmountRange, ordersStartDate, ordersEndDate]);

  // Query hook for brand orders
  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders
  } = useBrandOrders(brandId, ordersQueryParams);

  // Fetch all marketplaces for this brand (independent of current page orders)
  const { data: marketplacesData, isLoading: marketplacesLoading } = useMarketplacesList(brandId);

  // Extract orders and pagination from response
  const allOrders = ordersData?.orders || [];

  // Separate summary row from regular orders
  const ordersSummaryRow = allOrders.find(order => order.isSummaryRow);
  const orders = allOrders.filter(order => !order.isSummaryRow);

  const ordersPagination = ordersData?.pagination || {};
  const ordersTotalPages = ordersPagination.totalPages || 0;

  // Get all marketplaces from dedicated API endpoint (not from current page orders)
  const ordersMarketplaceOptions = useMemo(() => {
    const marketplaces = marketplacesData?.marketplaces || [];
    return marketplaces.map(mp => ({
      label: mp,
      value: mp
    }));
  }, [marketplacesData]);

  const ordersResourceName = {
    singular: 'order',
    plural: 'orders',
  };


  const { selectedResources: ordersSelectedResources, allResourcesSelected: ordersAllResourcesSelected, handleSelectionChange: ordersHandleSelectionChange } =
    useIndexResourceState(orders);

  // Orders callback functions
  const handleOrdersFiltersClearAll = useCallback(() => {
    setOrdersSearchQuery('');
    setOrdersStatusFilter([]);
    setOrdersMarketplaceFilter([]);
    setOrdersAmountRange([0, 5000]);
    setOrdersStartDate('');
    setOrdersEndDate('');
    setOrdersCurrentPage(1);
  }, []);

  // Standardized columns: Removed Discount. Added Items. Renamed Gross Revenue → Sales
  const handleOrdersSort = useCallback((headingIndex, direction) => {
    const sortFields = [
      'orderNumber',           // 0: Order
      'orderDate',             // 1: Date
      'marketplace',           // 2: Marketplace
      'totalWeight',           // 3: Weight
      'totalItems',            // 4: Items (NEW)
      'grossAmount',           // 5: Sales (renamed from Gross Revenue)
      'refundAmount',          // 6: Refunds
      'netSales',              // 7: Net Sales
      'appliedCommissionRate', // 8: Commission
      'appliedShippingFee',    // 9: Shipping Fees
      'appliedReturnFee',      // 10: Return Fee
      'payout',                // 11: Net Payout
    ];
    setOrdersSortBy(sortFields[headingIndex]);
    setOrdersSortDirection(direction);
    setOrdersCurrentPage(1);
  }, []);

  const handleOrdersPreviousPage = useCallback(() => {
    if (ordersCurrentPage > 1) {
      setOrdersCurrentPage(ordersCurrentPage - 1);
    }
  }, [ordersCurrentPage]);

  const handleOrdersNextPage = useCallback(() => {
    if (ordersCurrentPage < ordersTotalPages) {
      setOrdersCurrentPage(ordersCurrentPage + 1);
    }
  }, [ordersCurrentPage, ordersTotalPages]);

  // Resync single order handler
  const handleResyncOrder = useCallback(async (order) => {
    // Validate shopifyOrderId
    if (!order.shopifyOrderId) {
      setResyncBanner({
        tone: 'critical',
        message: 'Cannot resync: Order missing Shopify ID'
      });
      return;
    }

    try {
      setResyncingOrderId(order.id);
      setResyncBanner(null); // Clear any previous banners

      const response = await apiClient.request(
        `/orders/${order.shopifyOrderId}/resync`,
        {
          method: 'POST',
          body: JSON.stringify({ brandId }),
        }
      );

      if (response.success) {
        // Show success banner
        setResyncBanner({
          tone: 'success',
          message: `Order ${order.orderNumber} resynced successfully`
        });

        // Refetch orders to show updated data
        refetchOrders();

        // Clear any existing timeout
        if (resyncBannerTimeoutRef.current) {
          clearTimeout(resyncBannerTimeoutRef.current);
        }

        // Auto-dismiss success banner after 5 seconds
        resyncBannerTimeoutRef.current = setTimeout(() => {
          setResyncBanner(null);
          resyncBannerTimeoutRef.current = null;
        }, 5000);
      } else {
        throw new Error(response.error || 'Resync failed');
      }
    } catch (error) {
      console.error('Error resyncing order:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      setResyncBanner({
        tone: 'critical',
        message: `Failed to resync order: ${errorMsg}`
      });
    } finally {
      setResyncingOrderId(null);
    }
  }, [brandId, refetchOrders]);

  // Orders memoized data
  const ordersAppliedFilters = useMemo(() => {
    const filters = [];
    if (ordersSearchQuery) {
      filters.push({
        key: 'search',
        label: `Search: ${ordersSearchQuery}`,
        onRemove: () => setOrdersSearchQuery(''),
      });
    }
    if (ordersStatusFilter.length > 0) {
      filters.push({
        key: 'status',
        label: `Status: ${ordersStatusFilter.join(', ')}`,
        onRemove: () => setOrdersStatusFilter([]),
      });
    }
    if (ordersMarketplaceFilter.length > 0) {
      filters.push({
        key: 'marketplace',
        label: `Marketplace: ${ordersMarketplaceFilter.join(', ')}`,
        onRemove: () => setOrdersMarketplaceFilter([]),
      });
    }
    if (ordersAmountRange[0] > 0 || ordersAmountRange[1] < 5000) {
      filters.push({
        key: 'amount',
        label: `Amount: $${ordersAmountRange[0]} - $${ordersAmountRange[1]}`,
        onRemove: () => setOrdersAmountRange([0, 5000]),
      });
    }
    if (ordersStartDate || ordersEndDate) {
      const dateLabel = ordersStartDate && ordersEndDate
        ? `Date: ${ordersStartDate} to ${ordersEndDate}`
        : ordersStartDate
          ? `Date: From ${ordersStartDate}`
          : `Date: Until ${ordersEndDate}`;
      filters.push({
        key: 'dateRange',
        label: dateLabel,
        onRemove: () => {
          setOrdersStartDate('');
          setOrdersEndDate('');
        },
      });
    }
    return filters;
  }, [ordersSearchQuery, ordersStatusFilter, ordersMarketplaceFilter, ordersAmountRange, ordersStartDate, ordersEndDate]);

  // Table rows - includes both orders and refund line items
  const ordersRowMarkup = useMemo(() => {
    const allRows = orders.flatMap((order, orderIndex) => {
      const rows = [];

      const getTone = (value) => {
        if (value < 0) return 'critical';
        if (value > 0) return 'success';
        return 'base';
      };

      // Add main order row
      rows.push(
        <IndexTable.Row
          id={order.id}
          key={order.id}
          selected={ordersSelectedResources.includes(order.id)}
          position={orderIndex}
          tone={order.isRefundLineItem ? 'caution' : undefined}
        >
          {/* Order */}
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text variant="bodyMd" fontWeight="semibold" tone={order.isRefundLineItem ? 'caution' : undefined}>
                {order.orderNumber}{order.isRefundLineItem ? ' - REFUND' : ''}
              </Text>
              {order.isRefundLineItem && (
                <Box maxWidth='200px'>
                  <Tooltip content={order.refundReason || 'Refund processed'}>
                    <Text variant="bodySm" tone="subdued">
                      {order?.refundReason ? (order.refundReason.length > 40 ? `${order.refundReason.slice(0, 40)}...` : order.refundReason) : 'Refund processed'}
                    </Text>
                  </Tooltip>
                </Box>
              )}
            </BlockStack>
          </IndexTable.Cell>

          {/* Date */}
          <IndexTable.Cell>
            <Text variant="bodyMd">
              {order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }) : 'N/A'}
            </Text>
          </IndexTable.Cell>

          {/* Marketplace */}
          <IndexTable.Cell>
            <Badge tone={order.isRefundLineItem ? 'caution' : 'info'}>
              {order.marketplace || 'Direct'}
            </Badge>
          </IndexTable.Cell>

          {/* Weight */}
          <IndexTable.Cell>
            <Text variant="bodyMd">
              {order.totalWeight ? apiClient.formatWeight(order.totalWeight) : '-'}
            </Text>
          </IndexTable.Cell>

          {/* Items (NEW) */}
          <IndexTable.Cell>
            <Text variant="bodyMd">
              {order.totalItems || 0}
            </Text>
          </IndexTable.Cell>

          {/* Sales (renamed from Gross Revenue - value is gross, discounts not subtracted) */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="medium">
              {apiClient.formatCurrency(order.grossAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Refunds */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="medium" tone={order.isRefundLineItem && order.refundAmount ? 'critical' : undefined}>
              {apiClient.formatCurrency(order.refundAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Sales */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="medium" tone="success">
              {(() => {
                const grossAmount = order.grossAmount || 0;
                const discountAmount = order.discountAmount || 0;
                const refundAmount = order.refundAmount || 0;
                const netSales = Math.max(0, grossAmount - discountAmount - refundAmount);
                return apiClient.formatCurrency(netSales);
              })()}
            </Text>
          </IndexTable.Cell>


          {/* Commission */}
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text tone={getTone(order.appliedCommissionAmount)} variant="bodyMd">
                {apiClient.formatCurrency(order.appliedCommissionAmount)}
              </Text>
              <Text variant="bodySm" tone="subdued">
                {apiClient.formatPercentage(order.appliedCommissionRate || 0)}
              </Text>
            </BlockStack>
          </IndexTable.Cell>

          {/* Shipping Fees */}
          <IndexTable.Cell>
            <Text tone={getTone(order.appliedShippingFee || 0)} variant="bodyMd">
              {apiClient.formatCurrency(order.appliedShippingFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Return Fee */}
          <IndexTable.Cell>
            <Text tone={getTone(order.appliedReturnFee || 0)} variant="bodyMd">
              {apiClient.formatCurrency(order.appliedReturnFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Payout */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="medium" tone={order.isRefundLineItem && order.netAmount < 0 ? 'critical' : undefined}>
              {apiClient.formatCurrency(order.payout || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Actions */}
          <IndexTable.Cell>
            <InlineStack align="end" gap="200">
              <Button
                size="slim"
                icon={RefreshIcon}
                onClick={() => handleResyncOrder(order)}
                loading={resyncingOrderId === order.id}
                disabled={resyncingOrderId !== null || !order.shopifyOrderId}
                accessibilityLabel={`Resync order ${order.orderNumber}`}
              />
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      );

      return rows;
    });

    // Add summary row at the end if it exists
    if (ordersSummaryRow) {
      const summaryRowMarkup = (
        <IndexTable.Row
          id="SUMMARY_ROW"
          key="SUMMARY_ROW"
          selected={false}
          position={allRows.length}
          tone="subdued"
        >
          {/* Order - TOTAL label */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              TOTAL
            </Text>
          </IndexTable.Cell>

          {/* Date - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Marketplace - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Weight */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {ordersSummaryRow.totalWeight ? apiClient.formatWeight(ordersSummaryRow.totalWeight) : '-'}
            </Text>
          </IndexTable.Cell>

          {/* Items (NEW) */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {ordersSummaryRow.totalItems || 0}
            </Text>
          </IndexTable.Cell>

          {/* Sales (renamed from Gross Revenue) */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.grossAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Refunds */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.refundAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Sales */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.netAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Commission */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.appliedCommissionAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Shipping Fees */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.appliedShippingFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Return Fee */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.appliedReturnFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Payout */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold" tone={ordersSummaryRow.payout < 0 ? 'critical' : ordersSummaryRow.payout > 0 ? 'success' : 'base'}>
              {apiClient.formatCurrency(ordersSummaryRow.payout || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Actions - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>
        </IndexTable.Row>
      );

      return [...allRows, summaryRowMarkup];
    }

    return allRows;
  }, [orders, ordersSummaryRow, ordersSelectedResources, resyncingOrderId, handleResyncOrder]);

  // Available marketplaces state (using proper marketplace detection service)
  const [availableMarketplaceTags, setAvailableMarketplaceTags] = useState([]);
  const [availableMarketplacesLoading, setAvailableMarketplacesLoading] = useState(false);

  // Fetch available marketplaces using proper marketplace service
  useEffect(() => {
    if (!brandId) return;

    const fetchAvailableMarketplaces = async () => {
      try {
        setAvailableMarketplacesLoading(true);
        const response = await apiClient.request(`/brands/${brandId}/available-marketplaces`);

        // Don't filter out configured marketplaces here - let users configure any marketplace
        // The filtering should happen only in the dropdown options
        setAvailableMarketplaceTags(response.marketplaces || []);
      } catch (error) {
        console.error('Failed to fetch available marketplaces:', error);
        setAvailableMarketplaceTags([]);
      } finally {
        setAvailableMarketplacesLoading(false);
      }
    };

    fetchAvailableMarketplaces();
  }, [brandId]); // Remove marketplaceReturnFees dependency

  // Activity log state
  const [activityLog, setActivityLog] = useState([]);

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate, setSyncEndDate] = useState('');
  const [syncMaxMonths, setSyncMaxMonths] = useState(2); // Limited to 2 months due to Shopify API 60-day restriction
  const [syncForceSync, setSyncForceSync] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Refs for cleanup
  const syncPollingRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  const { data: brandAnalytics, isLoading: analyticsLoading, error: analyticsError } = useBrandAnalytics(
    brandId,
    getAnalyticsParams(),
    { enabled: !!brandId } // Always fetch analytics for overview cards
  );

  // Weight range options matching ShippingPricingTable
  const weightRangeOptions = [
    // 0-10 lb: 1-lb intervals
    { value: '0-1', minWeight: 0, maxWeight: 1 },
    { value: '1-2', minWeight: 1, maxWeight: 2 },
    { value: '2-3', minWeight: 2, maxWeight: 3 },
    { value: '3-4', minWeight: 3, maxWeight: 4 },
    { value: '4-5', minWeight: 4, maxWeight: 5 },
    { value: '5-6', minWeight: 5, maxWeight: 6 },
    { value: '6-7', minWeight: 6, maxWeight: 7 },
    { value: '7-8', minWeight: 7, maxWeight: 8 },
    { value: '8-9', minWeight: 8, maxWeight: 9 },
    { value: '9-10', minWeight: 9, maxWeight: 10 },
    // 10-20 lb: 2-lb intervals
    { value: '10-12', minWeight: 10, maxWeight: 12 },
    { value: '12-14', minWeight: 12, maxWeight: 14 },
    { value: '14-16', minWeight: 14, maxWeight: 16 },
    { value: '16-18', minWeight: 16, maxWeight: 18 },
    { value: '18-20', minWeight: 18, maxWeight: 20 },
    // 20-50 lb: 5-lb intervals
    { value: '20-25', minWeight: 20, maxWeight: 25 },
    { value: '25-30', minWeight: 25, maxWeight: 30 },
    { value: '30-35', minWeight: 30, maxWeight: 35 },
    { value: '35-40', minWeight: 35, maxWeight: 40 },
    { value: '40-45', minWeight: 40, maxWeight: 45 },
    { value: '45-50', minWeight: 45, maxWeight: 50 },
  ];

  const tabs = [
    { id: 'settings', content: 'Settings', accessibilityLabel: 'Configure brand settings' },
    { id: 'shipping', content: 'Shipping & Return', accessibilityLabel: 'Configure shipping and return fees' },
    { id: 'sync', content: 'Data Sync', accessibilityLabel: 'Sync historical order data' },
    { id: 'analytics', content: 'Analytics', accessibilityLabel: 'View brand performance analytics' },
    { id: 'orders', content: 'Orders', accessibilityLabel: 'View brand orders' },
    { id: 'adjustments', content: 'Commission Adjustments', accessibilityLabel: 'Manage commission adjustments' },
    { id: 'activity', content: 'Activity Log', accessibilityLabel: 'View configuration changes' }
  ];

  useEffect(() => {
    if (brandId) {
      fetchBrandData();
    }
  }, [brandId]);

  // Fetch sync status when sync tab is selected
  useEffect(() => {
    if (brandId && selectedTab === 2) { // sync tab
      fetchSyncStatus();
    }
  }, [brandId, selectedTab]);

  // Refetch orders when Orders tab is selected
  useEffect(() => {
    if (brandId && selectedTab === 4 && refetchOrders) {
      refetchOrders();
    }
  }, [brandId, selectedTab, refetchOrders]);

  // Cleanup function needs to be defined before use
  const cleanupSyncPolling = useCallback(() => {
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
    }
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Cleanup on component unmount and brand change
  useEffect(() => {
    return cleanupSyncPolling;
  }, [brandId]); // Clean up when brandId changes or component unmounts

  const fetchBrandData = useCallback(async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors

      if (!brandId || typeof brandId !== 'string') {
        throw new Error('Invalid brand ID provided');
      }

      const [brandData, activityData] = await Promise.all([
        apiClient.getBrand(brandId).catch(error => {
          // Only log detailed errors in development
          if (process.env.NODE_ENV === 'development') {
            console.error('Brand data fetch failed:', error);
          }
          throw new Error(`Failed to load brand details: ${error.message || 'Unknown error'}`);
        }),
        apiClient.getBrandActivity(brandId).catch(error => {
          // Only log warnings in development
          if (process.env.NODE_ENV === 'development') {
            console.warn('Activity data fetch failed, continuing without activity log:', error);
          }
          return []; // Return empty array if activity fetch fails
        })
      ]);

      // Validate brand data structure
      if (!brandData || typeof brandData !== 'object') {
        throw new Error('Invalid brand data received from server');
      }

      // Brand data fetched successfully - no need to log in production

      setBrand(brandData);
      const commissionRate = brandData.defaultCommissionRate || 0;
      setDefaultRate((commissionRate * 100).toString());
      setActivityLog(Array.isArray(activityData) ? activityData : []);

      // Initialize shipping enabled state from brand data
      setShippingEnabled(brandData.shippingEnabled !== false); // Default to true if undefined

      // Initialize return fees (both default and marketplace-specific) with global defaults support
      try {
        // Load return fees - API now returns global defaults and override indicators
        const returnFeeResponse = await apiClient.getDefaultReturnFee(brandId);

        // Set effective return fee (brand override or global default)
        setDefaultReturnFee((returnFeeResponse.defaultReturnFee || 0).toFixed(2));
        setHasDefaultReturnFee(returnFeeResponse.hasCustomFee || false);

        // Set marketplace fees (merged: brand overrides + global)
        setMarketplaceReturnFees(returnFeeResponse.marketplaceFees || {});

        // Track which are brand-specific overrides (for blue highlighting)
        setReturnFeeOverrideKeys(returnFeeResponse.overrideKeys || []);

        // Store global defaults for reference
        if (returnFeeResponse.globalDefaults) {
          setGlobalReturnFeeDefaults(returnFeeResponse.globalDefaults);
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Return fees loaded:', {
            defaultFee: returnFeeResponse.defaultReturnFee,
            hasBrandOverride: returnFeeResponse.hasCustomFee,
            overrideKeys: returnFeeResponse.overrideKeys,
            globalDefaults: returnFeeResponse.globalDefaults
          });
        }
      } catch (returnFeeError) {
        // Return fees are optional, don't fail the whole page load
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to load return fees:', returnFeeError.message);
        }
        setDefaultReturnFee('0.00');
        setHasDefaultReturnFee(false);
        setMarketplaceReturnFees({});
        setReturnFeeOverrideKeys([]);
        setGlobalReturnFeeDefaults({ default: 0, hasGlobalDefault: false, marketplaces: {} });
      }
    } catch (err) {
      // Only log detailed errors in development - don't expose sensitive details in production
      if (process.env.NODE_ENV === 'development') {
        console.error('BrandConfiguration: Brand data fetch error:', err.message || 'Unknown error');
      }
      const errorMessage = err.message || 'Failed to load brand data';
      setError(errorMessage);

      // Reset state on error
      setBrand(null);
      setActivityLog([]);
    } finally {
      setLoading(false);
    }
  }, [brandId, apiClient]);

  const updateDefaultCommission = async () => {
    if (!brand) return;

    // Enhanced validation using input security
    const rateValidation = validateNumeric(defaultRate, {
      min: 0,
      max: 100,
      allowNegative: false
    });

    if (!rateValidation.isValid) {
      setError(`Commission rate error: ${rateValidation.error}`);
      return;
    }

    try {
      setSaving(true);
      await apiClient.updateBrand(brand.id, {
        defaultCommissionRate: rateValidation.sanitized / 100
      });
      await fetchBrandData();
      setError(''); // Clear any previous errors on success
    } catch (err) {
      setError(err.message || 'Failed to update commission rate');
    } finally {
      setSaving(false);
    }
  };

  const updateShippingEnabled = async (enabled) => {
    if (!brand) return;

    try {
      setSaving(true);
      await apiClient.updateBrand(brand.id, {
        shippingEnabled: enabled
      });
      setShippingEnabled(enabled);
      await fetchBrandData(); // Refresh brand data
      setError(''); // Clear any previous errors on success
    } catch (err) {
      setError(err.message || 'Failed to update shipping configuration');
      // Revert the toggle on error
      setShippingEnabled(!enabled);
    } finally {
      setSaving(false);
    }
  };

  // Return fee management functions
  const updateDefaultReturnFee = async () => {
    if (!brand) return;

    const feeValidation = validateNumeric(defaultReturnFee, {
      min: 0,
      max: 1000,
      allowNegative: false
    });

    if (!feeValidation.isValid) {
      setError(`Return fee error: ${feeValidation.error}`);
      return;
    }

    try {
      setSaving(true);
      const response = await apiClient.updateDefaultReturnFee(brand.id, feeValidation.sanitized);

      // Check if backend returned isGlobal (value matched global, no override created/kept)
      if (response.isGlobal) {
        setHasDefaultReturnFee(false); // No brand-specific override
      } else {
        setHasDefaultReturnFee(true);
      }

      setError(''); // Clear any previous errors
      await fetchBrandData(); // Refresh data
    } catch (err) {
      setError(err.message || 'Failed to update default return fee');
    } finally {
      setSaving(false);
    }
  };

  const saveMarketplaceReturnFee = async (marketplace, fee) => {
    if (!brand) return;

    try {
      setSaving(true);
      const response = await apiClient.createMarketplaceReturnFee(brand.id, marketplace, fee);

      // Only update local state if backend actually created an override
      // If isGlobal is true, the value matched global and no override was created
      if (!response.isGlobal) {
        setMarketplaceReturnFees({
          ...marketplaceReturnFees,
          [marketplace]: parseFloat(fee)
        });
      }

      setShowReturnFeeModal(false);
      setEditingMarketplace('');
      setNewReturnFee('0.00'); // Reset for next use
      await fetchBrandData(); // Refresh brand data
    } catch (err) {
      setError(err.message || 'Failed to save marketplace return fee');
    } finally {
      setSaving(false);
    }
  };

  const deleteMarketplaceReturnFee = async (marketplace) => {
    if (!brand) return;
    if (!confirm(`Are you sure you want to delete the return fee for ${marketplace}?`)) return;

    try {
      setSaving(true);
      await apiClient.deleteMarketplaceReturnFee(brand.id, marketplace);
      const newFees = { ...marketplaceReturnFees };
      delete newFees[marketplace];
      setMarketplaceReturnFees(newFees);
      await fetchBrandData(); // Refresh brand data
    } catch (err) {
      setError(err.message || 'Failed to delete marketplace return fee');
    } finally {
      setSaving(false);
    }
  };

  // Reset default return fee to global default
  const handleResetDefaultFee = async () => {
    if (!brand) return;

    try {
      setResettingFees(true);
      // Use existing delete endpoint to remove brand-specific default return fee
      await apiClient.deleteDefaultReturnFee(brand.id);
      setShowResetDefaultFeeModal(false);
      await fetchBrandData(); // Refresh brand data
    } catch (err) {
      setError(err.message || 'Failed to reset default return fee');
    } finally {
      setResettingFees(false);
    }
  };

  // Reset all marketplace return fees to global defaults
  const handleResetMarketplaceFees = async () => {
    if (!brand) return;

    try {
      setResettingFees(true);
      const response = await apiClient.resetMarketplaceReturnFeesToDefault(brand.id);
      if (response.success) {
        setShowResetMarketplaceFeesModal(false);
        await fetchBrandData(); // Refresh brand data
      } else {
        throw new Error(response.message || 'Failed to reset marketplace return fees');
      }
    } catch (err) {
      setError(err.message || 'Failed to reset marketplace return fees');
    } finally {
      setResettingFees(false);
    }
  };

  const handleShippingPricingSave = async (changes, fullPricingData) => {
    if (!brand?.id) return;
    if (!changes || changes.length === 0) return;

    try {
      setSaving(true);
      setError('');

      // Transform changes into the format expected by the PATCH API
      const updates = changes.map(change => {
        const weightRange = weightRangeOptions.find(r => r.value === change.weightRangeId);
        return {
          shippingMethod: change.serviceId,
          weightMin: weightRange?.minWeight,
          weightMax: weightRange?.maxWeight,
          fee: change.newValue ? parseFloat(change.newValue) : null, // null = delete/reset
          oldFee: change.oldValue ? parseFloat(change.oldValue) : null // for audit
        };
      });

      // Use PATCH endpoint for granular updates
      const response = await apiClient.request(`/brands/${brand.id}/shipping-fees`, {
        method: 'PATCH',
        body: JSON.stringify({ updates }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to save shipping pricing');
      }
    } catch (err) {
      console.error('Error saving shipping pricing:', err);
      setError(`Failed to save shipping pricing: ${err.message}`);
      throw err; // Re-throw so the component can handle the error
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Page title="Brand Configuration" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <BlockStack gap="200">
                    <Text variant="bodyLg" fontWeight="medium">Loading brand configuration...</Text>
                    <Text variant="bodySm" tone="subdued">Fetching commission rates and shipping settings</Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!brand) {
    return (
      <Page title="Brand Configuration" fullWidth>
        <Layout>
          <Layout.Section>
            <EmptyState
              heading="Brand not found"
              content="The requested brand could not be found or you don't have access to it."
              image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
              action={{
                content: 'Back to Brand Selection',
                onAction: () => navigate('/')
              }}
            />
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Sync functions
  const fetchSyncStatus = async (retryCount = 0) => {
    try {
      const status = await apiClient.getSyncStatus(brandId);
      setSyncStatus(status);

      // Clear errors on successful fetch
      if (error && error.includes('Failed to get sync status')) {
        setError('');
      }

      return status;
    } catch (error) {
      console.error('Failed to fetch sync status:', error);

      // Retry logic for temporary failures with timeout cleanup
      if (retryCount < 2 && (error.status >= 500 || error.status === 503)) {
        // Wait before retry with exponential backoff
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s

        // Use Promise-based delay that can be cleaned up
        return new Promise((resolve) => {
          const timeoutId = setTimeout(async () => {
            // Clear the timeout ref once it executes
            if (retryTimeoutRef.current === timeoutId) {
              retryTimeoutRef.current = null;
            }
            const result = await fetchSyncStatus(retryCount + 1);
            resolve(result);
          }, delay);

          // Store timeout ID for cleanup
          retryTimeoutRef.current = timeoutId;
        });
      }

      // Only show error if not retrying or final retry failed
      if (retryCount >= 2 || error.status < 500) {
        if (error.status === 404) {
          setError('Brand not found or sync status unavailable.');
        } else if (error.status === 503) {
          setError('Sync service temporarily unavailable. Status will update when service is restored.');
        } else {
          setError(`Failed to get sync status: ${error.message || 'Unknown error occurred'}`);
        }
      }

      return null;
    }
  };

  const handleInitiateSync = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    setError('');

    try {
      const syncOptions = {
        startDate: syncStartDate || undefined,
        endDate: syncEndDate || undefined,
        maxMonths: syncMaxMonths,
        forceSync: syncForceSync
      };

      const result = await apiClient.syncOrders(brandId, syncOptions);

      // Update sync status immediately
      setSyncStatus({
        ...syncStatus,
        hasActiveJob: true,
        currentJob: {
          jobId: result.jobId,
          state: 'waiting',
          progress: 0
        }
      });

      // Poll for status updates
      pollSyncStatus();

    } catch (error) {
      console.error('Failed to initiate sync:', error);

      // Cleanup any running polls on error
      cleanupSyncPolling();
      setIsSyncing(false);

      // Enhanced error handling with specific messages
      if (error.status === 423) {
        // 423 Locked - distributed lock held by another admin
        const errorData = error.data || {};
        setError(`Sync locked by ${errorData.currentLockHolder || 'another admin'} since ${errorData.lockAcquiredAt ? new Date(errorData.lockAcquiredAt).toLocaleString() : 'unknown time'}. Use "Force Sync" to override.`);
      } else if (error.status === 409) {
        setError('Sync already in progress for this brand. Use "Force Sync" to override.');
      } else if (error.status === 400) {
        // Handle validation errors
        const errorMessage = error.message || 'Invalid sync parameters';
        if (errorMessage.includes('session')) {
          setError('Shopify connection issue: The app may need to be reinstalled for this brand.');
        } else if (errorMessage.includes('date')) {
          setError(`Date validation error: ${errorMessage}`);
        } else if (errorMessage.includes('inactive')) {
          setError('Cannot sync inactive brand. Please activate the brand first.');
        } else {
          setError(`Validation error: ${errorMessage}`);
        }
      } else if (error.status === 503) {
        setError('Sync service temporarily unavailable. Please try again in a moment.');
      } else if (error.status >= 500) {
        setError('Server error occurred. Please try again or contact support if the issue persists.');
      } else if (!navigator.onLine) {
        setError('No internet connection. Please check your network and try again.');
      } else {
        setError(`Failed to start sync: ${error.message || 'Unknown error occurred'}`);
      }
    }
  };

  const pollSyncStatus = () => {
    // Clean up any existing polling first
    cleanupSyncPolling();

    let pollAttempts = 0;
    const maxPollAttempts = 360; // 30 minutes at 5-second intervals

    syncPollingRef.current = setInterval(async () => {
      try {
        pollAttempts++;

        // Stop polling after max attempts to prevent indefinite polling
        if (pollAttempts >= maxPollAttempts) {
          cleanupSyncPolling();
          setIsSyncing(false);
          setError('Sync monitoring stopped after 30 minutes. Please check sync status manually.');
          return;
        }

        const status = await fetchSyncStatus();

        if (!status?.hasActiveJob) {
          cleanupSyncPolling();
          setIsSyncing(false);

          // Clear the current job from state when sync completes
          setSyncStatus(prevStatus => ({
            ...status,
            currentJob: null
          }));

          // Show completion message if sync was successful
          if (status?.brandSyncStatus === 'completed') {
            // Clear any previous errors on successful completion
            setError('');
          }
        }
      } catch (error) {
        console.error('Error polling sync status:', error);

        // Don't immediately stop polling on single error - could be temporary network issue
        if (pollAttempts >= 3) {
          // Only show error after multiple attempts
          setError(`Failed to get sync status: ${error.message}`);
        }

        // Stop polling after 5 consecutive errors
        if (pollAttempts >= 5) {
          cleanupSyncPolling();
          setIsSyncing(false);
        }
      }
    }, 5000);
  };

  const canInitiateSync = () => {
    return brand?.isActive && !isSyncing && (!syncStatus?.hasActiveJob || syncForceSync);
  };

  const renderSyncTab = () => (
    <Layout>
      <Layout.Section>
        <Card>
          <Box padding="500">
            <BlockStack gap="500">
              {/* Well-spaced Header with Status */}
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3" fontWeight="semibold">
                    Order Data Sync
                  </Text>
                  <Text variant="bodyMd" tone="subdued">
                    Sync historical orders and recalculate billing with current settings
                  </Text>
                </BlockStack>
                <InlineStack gap="300" blockAlign="center">
                  {syncStatus?.lastSyncDate && !syncStatus.hasActiveJob && (
                    <Text variant="bodySm" tone="subdued">
                      Last sync: {new Date(syncStatus.lastSyncDate).toLocaleDateString()}
                    </Text>
                  )}
                  {syncStatus && (
                    <Badge tone={syncStatus.hasActiveJob ? 'info' : 'success'}>
                      {syncStatus.hasActiveJob ? 'Syncing' : 'Ready'}
                    </Badge>
                  )}
                </InlineStack>
              </InlineStack>

              {/* Balanced Progress Indicator */}
              {(syncStatus?.hasActiveJob || isSyncing) && syncStatus?.currentJob && (
                <Box padding="400" background="bg-surface-info-subdued" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="300" blockAlign="center">
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #e1e3e5',
                        borderTop: '2px solid #006fff',
                        borderRadius: '50%',
                        animation: 'syncSpin 1s linear infinite'
                      }} />
                      <Text variant="bodyMd" fontWeight="medium">Syncing orders...</Text>
                    </InlineStack>
                    <Badge tone="info">
                      {Math.round(syncStatus.currentJob.progress || 0)}%
                    </Badge>
                  </InlineStack>
                  <style>{`
                    @keyframes syncSpin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </Box>
              )}

              {/* Balanced Configuration Form */}
              <BlockStack gap="400">
                {/* Configuration Fields Section */}
                <BlockStack gap="300">
                  <Text variant="bodyMd" fontWeight="medium">Configuration</Text>
                  <InlineStack gap="400" align="start" blockAlign="end" wrap={false}>
                    <Box width="150px">
                      <TextField
                        label="Start Date"
                        type="date"
                        value={syncStartDate}
                        onChange={setSyncStartDate}
                        disabled={isSyncing}
                      />
                    </Box>
                    <Box width="150px">
                      <TextField
                        label="End Date"
                        type="date"
                        value={syncEndDate}
                        onChange={setSyncEndDate}
                        max={formatDateLocal(new Date())}
                        disabled={isSyncing}
                      />
                    </Box>
                    <Box width="120px">
                      <TextField
                        label="Max Months"
                        type="number"
                        value={syncMaxMonths.toString()}
                        onChange={(value) => setSyncMaxMonths(parseInt(value) || 2)}
                        min="1"
                        // max="2"
                        disabled={isSyncing}
                      />
                    </Box>
                  </InlineStack>
                </BlockStack>

                {/* Options Section */}
                <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                  <Checkbox
                    label="Force recalculation with current settings"
                    checked={syncForceSync}
                    onChange={setSyncForceSync}
                    disabled={isSyncing}
                    helpText="Recalculate all fees using your latest commission and shipping configuration"
                  />
                </Box>

                {/* Action Buttons Section */}
                <InlineStack gap="300" align="start">
                  <Button
                    variant="primary"
                    size="large"
                    onClick={handleInitiateSync}
                    loading={isSyncing}
                    disabled={!canInitiateSync()}
                  >
                    {syncStatus?.hasActiveJob && !syncForceSync ? 'Syncing...' : 'Start Sync'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={fetchSyncStatus}
                    disabled={isSyncing}
                  >
                    Refresh Status
                  </Button>
                </InlineStack>
              </BlockStack>

              {/* Error Display */}
              {(error || (syncStatus?.lastSyncError && !syncStatus.hasActiveJob)) && (
                <Banner tone="critical">
                  <Text>{error || syncStatus.lastSyncError}</Text>
                </Banner>
              )}
            </BlockStack>
          </Box>
        </Card>
      </Layout.Section>

      {/* Sync Coverage Overview */}
      <Layout.Section>
        {/* <SyncCoverageSummary
          syncCoverage={syncCoverage}
          isLoading={syncCoverageLoading}
          error={syncCoverageError}
          showTitle={true}
        /> */}
      </Layout.Section>
    </Layout>
  );

  const renderShippingConfiguration = () => {
    return (
      <BlockStack gap="600">
        {/* Shipping Configuration Toggle */}
        <Card>
          <Box padding="600">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="200">
                <Text variant="headingMd" as="h3">Shipping Configuration</Text>
                <Text variant="bodyMd" tone="subdued">
                  {shippingEnabled
                    ? "Configure BoxC shipping pricing for different weight ranges"
                    : "Shipping rules are disabled for this brand."
                  }
                </Text>
              </BlockStack>
              <InlineStack gap="200" blockAlign="center">
                <Text variant="bodyMd" fontWeight="medium">
                  {shippingEnabled ? 'Enabled' : 'Disabled'}
                </Text>
                <Button
                  variant={shippingEnabled ? 'secondary' : 'primary'}
                  tone={shippingEnabled ? 'critical' : 'success'}
                  onClick={() => updateShippingEnabled(!shippingEnabled)}
                  loading={saving}
                  disabled={saving || isSyncing}
                >
                  {shippingEnabled ? 'Disable Shipping' : 'Enable Shipping'}
                </Button>
              </InlineStack>
            </InlineStack>
          </Box>
        </Card>

        {/* BoxC Shipping Pricing Table - Only show when enabled */}
        {shippingEnabled && (
          <Card>
            <Box padding="600">
              <BlockStack gap="400">
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">BoxC Shipping Pricing</Text>
                  <Text variant="bodyMd" tone="subdued">
                    Override pricing for BoxC services across different weight ranges. Click any price cell to edit.
                  </Text>
                </BlockStack>
                <ShippingPricingTable
                  brand={brand}
                  onSave={handleShippingPricingSave}
                  loading={saving}
                  disabled={isSyncing}
                />
              </BlockStack>
            </Box>
          </Card>
        )}

        {/* Default Return Fee */}
        <Card>
          <Box padding="600">
            <BlockStack gap="500">
              <BlockStack gap="200">
                <Text variant="headingMd" as="h3">Default Return Fee</Text>
                <Text variant="bodyMd" tone="subdued">
                  Set a default return fee applied to all orders. Can be overridden per marketplace below.
                </Text>
              </BlockStack>

              {/* Show global default info if brand doesn't have override */}
              {!hasDefaultReturnFee && globalReturnFeeDefaults.hasGlobalDefault && (
                <Banner tone="info">
                  <Text variant="bodySm">
                    Using global default: <strong>${globalReturnFeeDefaults.default.toFixed(2)}</strong>.
                    Set a brand-specific fee below to override this.
                  </Text>
                </Banner>
              )}

              <Box
                padding="400"
                borderRadius="200"
                background={returnFeeOverrideKeys.includes('default') ? 'bg-surface-info' : 'bg-surface'}
                borderColor={returnFeeOverrideKeys.includes('default') ? 'border-info' : 'border'}
                borderWidth="025"
              >
                <InlineStack gap="400" blockAlign="center">
                  <Box minWidth="200px">
                    <TextField
                      label="Default Return Fee"
                      type="number"
                      value={defaultReturnFee}
                      onChange={setDefaultReturnFee}
                      prefix="$"
                      min="0"
                      max="1000"
                      step="0.01"
                      helpText={returnFeeOverrideKeys.includes('default')
                        ? "Brand-specific override (global: $" + globalReturnFeeDefaults.default.toFixed(2) + ")"
                        : "Applied to all marketplaces unless overridden"}
                    />
                  </Box>
                  <Button
                    variant="primary"
                    onClick={updateDefaultReturnFee}
                    loading={saving}
                    disabled={saving || isSyncing}
                    icon={CheckCircleIcon}
                    size="large"
                  >
                    {hasDefaultReturnFee ? 'Update Fee' : 'Set Default Fee'}
                  </Button>
                  {returnFeeOverrideKeys.includes('default') && (
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowResetDefaultFeeModal(true)}
                      disabled={saving || isSyncing || resettingFees}
                    >
                      Reset to Default
                    </Button>
                  )}
                </InlineStack>
                {returnFeeOverrideKeys.includes('default') && (
                  <Box paddingBlockStart="200">
                    <Text variant="bodySm" tone="info">
                      This brand has a custom override. Global default is ${globalReturnFeeDefaults.default.toFixed(2)}.
                    </Text>
                  </Box>
                )}
              </Box>

              {hasDefaultReturnFee && !returnFeeOverrideKeys.includes('default') && (
                <Banner tone="info">
                  <Text variant="bodySm">
                    Current default return fee: <strong>${parseFloat(defaultReturnFee).toFixed(2)}</strong>.
                    This applies to all orders unless a marketplace-specific override exists below.
                  </Text>
                </Banner>
              )}

              {saving && <ProgressBar progress={undefined} size="small" />}
            </BlockStack>
          </Box>
        </Card>

        {/* Marketplace Return Fee Overrides */}
        <Card>
          <Box padding="600">
            <BlockStack gap="500">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Marketplace Return Fee Overrides</Text>
                  <Text variant="bodyMd" tone="subdued">
                    Override the default return fee for specific marketplaces.
                    <span style={{ color: '#0ea5e9' }}> Blue rows</span> indicate brand-specific overrides.
                  </Text>
                </BlockStack>
                <InlineStack gap="200">
                  <Button
                    variant="primary"
                    icon={PlusIcon}
                    onClick={() => setShowReturnFeeModal(true)}
                    disabled={marketplacesLoading || saving || isSyncing}
                  >
                    Add Marketplace Fee
                  </Button>
                  {returnFeeOverrideKeys.filter(key => key !== 'default').length > 0 && (
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowResetMarketplaceFeesModal(true)}
                      disabled={saving || isSyncing || resettingFees}
                    >
                      Reset All to Default ({returnFeeOverrideKeys.filter(key => key !== 'default').length})
                    </Button>
                  )}
                </InlineStack>
              </InlineStack>

              {/* Marketplace Return Fees */}
              {Object.keys(marketplaceReturnFees).length > 0 && (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'text', 'text']}
                  headings={['Marketplace', 'Return Fee', 'Source', 'Actions']}
                  rows={Object.entries(marketplaceReturnFees).map(([marketplace, fee]) => {
                    const isBrandOverride = returnFeeOverrideKeys.includes(marketplace);
                    const globalFee = globalReturnFeeDefaults.marketplaces?.[marketplace];
                    return [
                      <Box
                        key={`mp-${marketplace}`}
                        padding="100"
                        borderRadius="100"
                        background={isBrandOverride ? 'bg-surface-info' : 'bg-surface'}
                      >
                        <Badge tone={isBrandOverride ? 'info' : 'default'}>{marketplace}</Badge>
                      </Box>,
                      <Box
                        key={`fee-${marketplace}`}
                        padding="100"
                        borderRadius="100"
                        background={isBrandOverride ? 'bg-surface-info' : 'bg-surface'}
                      >
                        {formatCurrency(parseFloat(fee) || 0)}
                      </Box>,
                      <Box
                        key={`src-${marketplace}`}
                        padding="100"
                        borderRadius="100"
                        background={isBrandOverride ? 'bg-surface-info' : 'bg-surface'}
                      >
                        <Text variant="bodySm" tone={isBrandOverride ? 'info' : 'subdued'}>
                          {isBrandOverride ? 'Brand Override' : 'Global Default'}
                          {isBrandOverride && globalFee !== undefined && ` (global: $${globalFee.toFixed(2)})`}
                        </Text>
                      </Box>,
                      isBrandOverride ? (
                        <Button
                          key={`del-${marketplace}`}
                          variant="tertiary"
                          tone="critical"
                          size="micro"
                          onClick={() => deleteMarketplaceReturnFee(marketplace)}
                          loading={saving}
                        >
                          Remove
                        </Button>
                      ) : (
                        <Text key={`na-${marketplace}`} variant="bodySm" tone="subdued">—</Text>
                      )
                    ];
                  })}
                />
              )}

              {Object.keys(marketplaceReturnFees).length === 0 && (
                <Box padding="400" background="bg-fill-info-secondary" borderRadius="200">
                  <Text variant="bodySm" tone="subdued">
                    {hasDefaultReturnFee || globalReturnFeeDefaults.hasGlobalDefault
                      ? `No marketplace overrides configured. All marketplaces use the default return fee of $${parseFloat(defaultReturnFee).toFixed(2)}.`
                      : 'No marketplace overrides configured. No default return fee is set.'}
                  </Text>
                </Box>
              )}
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    );
  };

  const renderAnalytics = () => (
    <BlockStack gap="600">
      {/* Brand Analytics Cards */}
      <Grid gap="400">
        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }} style={{ display: 'flex' }}>
          <OrdersCard
            title="Orders"
            value={brandAnalytics?.totalOrders || 0}
            loading={analyticsLoading}
            error={analyticsError}
            // subtitle={`AOV: ${apiClient.formatCurrency(brandAnalytics?.averageOrderValue || 0)}`}
            icon={OrderIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }} style={{ display: 'flex' }}>
          <RevenueCard
            title="Gross Revenue"
            value={brandAnalytics?.totalGrossRevenue || 0}
            loading={analyticsLoading}
            error={analyticsError}
            // change={brandAnalytics && {
            //   percentage: brandAnalytics.monthlyGrowth || 0,
            //   direction: brandAnalytics.monthlyGrowth >= 0 ? 'up' : 'down',
            //   period: `from last ${analyticsFilter} days`,
            // }}
            // subtitle={`${brand.name} performance`}
            icon={CashDollarIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }} style={{ display: 'flex' }}>
          <RevenueCard
            title="Discounts"
            value={brandAnalytics?.totalDiscounts || 0}
            loading={analyticsLoading}
            error={analyticsError}
            // change={brandAnalytics && {
            //   percentage: brandAnalytics.monthlyGrowth || 0,
            //   direction: brandAnalytics.monthlyGrowth >= 0 ? 'up' : 'down',
            //   period: `from last ${analyticsFilter} days`,
            // }}
            // subtitle={`${brand.name} performance`}
            icon={CashDollarIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }} style={{ display: 'flex' }}>
          <MetricCard
            title="Refunds"
            value={brandAnalytics?.totalRefunds || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => (() => {
              try {
                return apiClient.formatCurrency(value);
              } catch (error) {
                return '$0.00';
              }
            })()}
            // change={{
            //   percentage: 0,
            //   direction: 'neutral',
            //   period: `vs. previous ${analyticsFilter} days`,
            // }}
            // subtitle="Total amount refunded"
            icon={ReceiptRefundIcon}
            color="critical"
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }} style={{ display: 'flex' }}>
          <RevenueCard
            title="Net Sales"
            value={brandAnalytics?.totalRevenue || 0}
            loading={analyticsLoading}
            error={analyticsError}
            // change={brandAnalytics && {
            //   percentage: brandAnalytics.monthlyGrowth || 0,
            //   direction: brandAnalytics.monthlyGrowth >= 0 ? 'up' : 'down',
            //   period: `from last ${analyticsFilter} days`,
            // }}
            // subtitle={`${brand.name} performance`}
            icon={CashDollarIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }} style={{ display: 'flex' }}>
          <CommissionCard
            title="Commission + Adjustments"
            value={brandAnalytics?.totalCommission || 0}
            loading={analyticsLoading}
            error={analyticsError}
            // change={brandAnalytics && {
            //   percentage: brandAnalytics.commissionGrowth || 0,
            //   direction: brandAnalytics.commissionGrowth >= 0 ? 'up' : 'down',
            //   period: `from last ${analyticsFilter} days`,
            // }}
            // subtitle={`Commission:${brandAnalytics?.commissionBreakdown.baseCommission} + Adjustments:${brandAnalytics?.commissionBreakdown.totalAdjustmentAmount}`}
            icon={CashDollarIcon}
            tooltip={`Commission:${brandAnalytics?.commissionBreakdown.baseCommission} | Adjustments: ${brandAnalytics?.commissionBreakdown.totalAdjustmentAmount}`}
          />
        </Grid.Cell>


        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }}>
          <MetricCard
            title="Shipping Fees"
            value={brandAnalytics?.totalShippingFees || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => (() => {
              try {
                return apiClient.formatCurrency(value);
              } catch (error) {
                return '$0.00';
              }
            })()}
            // change={{
            //   percentage: 0,
            //   direction: 'neutral',
            //   period: `vs. previous ${analyticsFilter} days`,
            // }}
            icon={StoreIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }}>
          <MetricCard
            title="Return Fees"
            value={brandAnalytics?.totalReturnFees || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => (() => {
              try {
                return apiClient.formatCurrency(value);
              } catch (error) {
                return '$0.00';
              }
            })()}
            // change={{
            //   percentage: 0,
            //   direction: 'neutral',
            //   period: `vs. previous ${analyticsFilter} days`,
            // }}
            icon={ReturnIcon}
            color="warning"
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 4, xl: 2 }} style={{ display: 'flex' }}>
          <RevenueCard
            title="Net Payout"
            value={brandAnalytics?.totalPayout || 0}
            loading={analyticsLoading}
            error={analyticsError}
            // change={brandAnalytics && {
            //   percentage: brandAnalytics.monthlyGrowth || 0,
            //   direction: brandAnalytics.monthlyGrowth >= 0 ? 'up' : 'down',
            //   period: `from last ${analyticsFilter} days`,
            // }}
            // subtitle={`${brand.name} performance`}
            icon={CashDollarIcon}
          />
        </Grid.Cell>

      </Grid>

      {/* Period Filter */}
      <Card>
        <Box padding="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd">Performance Period</Text>
            <Select
              label="Time Period"
              labelHidden
              options={[
                { label: 'Last Month', value: 'lastMonth' },
                { label: 'This Month to Date', value: 'thisMonth' },
                { label: 'Last 7 days', value: '7' },
                { label: 'Last 30 days', value: '30' },
                { label: 'Last 90 days', value: '90' },
                { label: 'Last 365 days', value: '365' },
                { label: customStartDate && customEndDate ? `${customStartDate} to ${customEndDate}` : 'Custom Range...', value: 'custom' },
              ]}
              value={dateRangeMode === 'preset' ? analyticsFilter : dateRangeMode}
              onChange={(value) => {
                if (value === 'lastMonth' || value === 'thisMonth') {
                  setDateRangeMode(value);
                } else if (value === 'custom') {
                  // Open modal for custom date selection
                  // Initialize DatePicker with existing dates if available
                  if (customStartDate && customEndDate) {
                    const start = new Date(customStartDate);
                    const end = new Date(customEndDate);
                    setSelectedDates({ start, end });
                    setDatePickerMonth(start.getMonth());
                    setDatePickerYear(start.getFullYear());
                  } else {
                    // Default to current date
                    const today = new Date();
                    setSelectedDates({ start: today, end: today });
                    setDatePickerMonth(today.getMonth());
                    setDatePickerYear(today.getFullYear());
                  }
                  setShowCustomDateModal(true);
                } else {
                  setAnalyticsFilter(value);
                  setDateRangeMode('preset');
                }
              }}
            />
          </InlineStack>
        </Box>
      </Card>

      {/* Custom Date Range Modal */}
      <Modal
        open={showCustomDateModal}
        onClose={() => {
          setShowCustomDateModal(false);
        }}
        title="Select Custom Date Range"
        primaryAction={{
          content: 'Apply',
          onAction: () => {
            // Convert Date objects to YYYY-MM-DD strings using local timezone
            const startStr = formatDateLocal(selectedDates.start);
            const endStr = formatDateLocal(selectedDates.end);
            setCustomStartDate(startStr);
            setCustomEndDate(endStr);
            setDateRangeMode('custom');
            setShowCustomDateModal(false);
          },
          disabled: !selectedDates.start || !selectedDates.end
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowCustomDateModal(false);
            }
          }
        ]}
      >
        <Modal.Section>
          <Box paddingBlockEnd="400">
            <Text variant="bodySm" tone="subdued">
              Select a start date and end date for your custom range
            </Text>
          </Box>
          <DatePicker
            month={datePickerMonth}
            year={datePickerYear}
            onChange={setSelectedDates}
            onMonthChange={(month, year) => {
              setDatePickerMonth(month);
              setDatePickerYear(year);
            }}
            selected={selectedDates}
            allowRange
          />
        </Modal.Section>
      </Modal>

      {/* Brand-Specific Marketplace Breakdown */}
      <MarketplaceBreakdown
        brandId={brandId}
        period={getAnalyticsParams().days || parseInt(analyticsFilter)}
        dateRange={getAnalyticsParams().startDate && getAnalyticsParams().endDate ? {
          startDate: getAnalyticsParams().startDate,
          endDate: getAnalyticsParams().endDate
        } : null}
        title={`${brand.name} - Marketplace Performance`}
        onMarketplaceSelect={(marketplace) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Selected marketplace for brand:', marketplace, brand.name);
          }
        }}
      />
    </BlockStack>
  );

  const renderOrders = () => {
    // Filter options
    const ordersStatusOptions = [
      { label: 'Paid', value: 'PAID' },
      { label: 'Pending', value: 'PENDING' },
      { label: 'Refunded', value: 'REFUNDED' },
      { label: 'Partially Refunded', value: 'PARTIALLY_REFUNDED' },
      { label: 'Fulfilled', value: 'FULFILLED' },
    ];

    const ordersFilters = [
      {
        key: 'status',
        label: 'Status',
        filter: (
          <ChoiceList
            title="Status"
            titleHidden
            choices={ordersStatusOptions}
            selected={ordersStatusFilter}
            onChange={setOrdersStatusFilter}
            allowMultiple
          />
        ),
        shortcut: true,
      },
      {
        key: 'marketplace',
        label: 'Marketplace',
        filter: (
          <ChoiceList
            title="Marketplace"
            titleHidden
            choices={ordersMarketplaceOptions}
            selected={ordersMarketplaceFilter}
            onChange={setOrdersMarketplaceFilter}
            allowMultiple
          />
        ),
        shortcut: true,
      },
      {
        key: 'amount',
        label: 'Amount Range',
        filter: (
          <RangeSlider
            label="Net Amount Range"
            value={ordersAmountRange}
            onChange={setOrdersAmountRange}
            output
            min={0}
            max={5000}
            step={50}
            prefix="$"
          />
        ),
      },
      {
        key: 'dateRange',
        label: 'Date Range',
        filter: (
          <BlockStack gap="300">
            <TextField
              label="Start Date"
              type="date"
              value={ordersStartDate}
              onChange={setOrdersStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={ordersEndDate}
              onChange={setOrdersEndDate}
              autoComplete="off"
              min={ordersStartDate}
            />
            <Button
              size="slim"
              onClick={() => {
                const today = new Date();
                const thirtyDaysAgo = new Date(today);
                thirtyDaysAgo.setDate(today.getDate() - 30);
                setOrdersStartDate(formatDateLocal(thirtyDaysAgo));
                setOrdersEndDate(formatDateLocal(today));
              }}
            >
              Last 30 days
            </Button>
          </BlockStack>
        ),
      },
    ];

    // Table headings - exact 10 columns as requested
    const ordersHeadings = [
      { title: 'Order', alignment: 'start' },
      { title: 'Date', alignment: 'start' },
      { title: 'Marketplace', alignment: 'start' },
      { title: 'Weight', alignment: 'start' },
      { title: 'Items', alignment: 'start' },
      { title: 'Sales', alignment: 'start' },
      { title: 'Refunds', alignment: 'start' },
      { title: 'Net Sales', alignment: 'start' },
      { title: 'Commission', alignment: 'start' },
      { title: 'Shipping Fees', alignment: 'start' },
      { title: 'Return Fee', alignment: 'start' },
      { title: 'Net Payout', alignment: 'start' },
      { title: 'Actions', alignment: 'end' },
    ];

    const ordersEmptyStateMarkup = (
      <EmptyState
        heading="No orders found"
        content={
          ordersAppliedFilters.length > 0
            ? "Try adjusting your search or filter criteria to find orders."
            : "Orders will appear here once they're synced from this brand."
        }
        image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
        action={ordersAppliedFilters.length > 0 ? {
          content: 'Clear filters',
          onAction: handleOrdersFiltersClearAll,
        } : undefined}
      />
    );

    if (ordersError) {
      return (
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load orders. {ordersError.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      );
    }

    return (
      <Layout>
        {/* Resync Banner */}
        {resyncBanner && (
          <Layout.Section>
            <Banner
              tone={resyncBanner.tone}
              onDismiss={() => setResyncBanner(null)}
            >
              {resyncBanner.message}
            </Banner>
          </Layout.Section>
        )}

        {/* Orders Table */}
        <Layout.Section>
          <Card>
            {/* Filters integrated into table card */}
            <Box padding="200">
              <Filters
                queryValue={ordersSearchQuery}
                queryPlaceholder="Search orders..."
                onQueryChange={setOrdersSearchQuery}
                onQueryClear={() => setOrdersSearchQuery('')}
                filters={ordersFilters}
                appliedFilters={ordersAppliedFilters}
                onClearAll={handleOrdersFiltersClearAll}
                disabled={ordersLoading}
              />
            </Box>
            <Divider />
            <IndexTable
              resourceName={ordersResourceName}
              itemCount={ordersRowMarkup.length}
              headings={ordersHeadings}
              emptyState={ordersEmptyStateMarkup}
              loading={ordersLoading}
              selectable={false}
              sortable={[true, true, true, true, true, true, true, true, true, true, true, true, false]}
              sortDirection={ordersSortDirection}
              sortColumnIndex={Math.max(0, ordersHeadings.findIndex(h =>
                h?.title?.toLowerCase().includes(ordersSortBy?.toLowerCase()) ||
                (ordersSortBy === 'orderNumber' && h?.title === 'Order') ||
                (ordersSortBy === 'orderDate' && h?.title === 'Date') ||
                (ordersSortBy === 'totalWeight' && h?.title === 'Weight') ||
                (ordersSortBy === 'totalItems' && h?.title === 'Items') ||
                (ordersSortBy === 'grossAmount' && h?.title === 'Sales') ||
                (ordersSortBy === 'refundAmount' && h?.title === 'Refunds') ||
                (ordersSortBy === 'netSales' && h?.title === 'Net Sales') ||
                (ordersSortBy === 'appliedCommissionRate' && h?.title === 'Commission') ||
                (ordersSortBy === 'appliedShippingFee' && h?.title === 'Shipping Fees') ||
                (ordersSortBy === 'appliedReturnFee' && h?.title === 'Return Fee') ||
                (ordersSortBy === 'payout' && h?.title === 'Net Payout')
              ))}
              onSort={handleOrdersSort}
              hasZebraStriping={true}
            >
              {ordersRowMarkup}
            </IndexTable>

            {/* Table Footer with Summary and Pagination */}
            {(!ordersLoading && orders.length > 0) && (
              <>
                <Divider />
                <Box padding="400">
                  <InlineStack gap="800" align="space-between" blockAlign="center">

                    {/* Pagination */}
                    {ordersTotalPages > 1 && (
                      <Box style={{ flexShrink: 0 }}>
                        <InlineStack gap="200" blockAlign="center">
                          <Button
                            disabled={ordersCurrentPage <= 1}
                            onClick={handleOrdersPreviousPage}
                            size="slim"
                          >
                            Previous
                          </Button>
                          <Text variant="bodySm" tone="subdued">
                            {ordersCurrentPage} of {ordersTotalPages}
                          </Text>
                          <Button
                            disabled={ordersCurrentPage >= ordersTotalPages}
                            onClick={handleOrdersNextPage}
                            size="slim"
                          >
                            Next
                          </Button>
                        </InlineStack>
                      </Box>
                    )}
                  </InlineStack>
                </Box>
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    );
  };

  const renderActivityLog = () => (
    <Card>
      <Box padding="600">
        <BlockStack gap="500">
          <BlockStack gap="200">
            <Text variant="headingMd" as="h3">Configuration Activity Log</Text>
            <Text variant="bodyMd" tone="subdued">
              Track all changes made to this brand's commission and shipping settings
            </Text>
          </BlockStack>

          {activityLog.length > 0 ? (
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text']}
              headings={['Type', 'Action', 'Details', 'User', 'Date']}
              rows={activityLog.map(activity => [
                <Badge tone={activity.type === 'commission' ? 'info' : 'success'}>
                  {activity.type}
                </Badge>,
                activity.action,
                formatActivityDetails(activity.action, activity.details),
                activity.user,
                (() => {
                  const date = new Date(activity.timestamp);
                  return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleString();
                })()
              ])}
            />
          ) : (
            <EmptyState
              heading="No activity recorded"
              content="Configuration changes and updates will appear here for audit purposes."
            />
          )}
        </BlockStack>
      </Box>
    </Card>
  );

  return (
    <Page
      title={`${brand.name} Configuration`}
      subtitle={`Configure commission rates and shipping fees for ${brand.shopDomain}`}
      breadcrumbs={[{ content: 'Brand Selection', url: '/' }]}
      compactTitle
      fullWidth
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* Sync In Progress Global Banner */}
        {isSyncing && (
          <Layout.Section>
            <Banner tone="info" title="Sync In Progress">
              <BlockStack gap="200">
                <Text>
                  Order data synchronization is currently running. Some configuration options are temporarily disabled.
                </Text>
                <Text variant="bodySm" tone="subdued">
                  You can monitor progress in the Data Sync tab. The sync will complete automatically.
                </Text>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        {/* Brand Header */}
        <Layout.Section>
          <Card>
            <Box padding="600">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="400" blockAlign="center">
                  <Box
                    background={brand.isActive ? "bg-fill-success-secondary" : "bg-fill-critical-secondary"}
                    padding="400"
                    borderRadius="50"
                  >
                    <Icon source={StoreIcon} tone={brand.isActive ? "success" : "critical"} />
                  </Box>
                  <BlockStack gap="100">
                    <Text variant="headingLg" as="h1">{brand.name}</Text>
                    <InlineStack gap="300" align="start">
                      <Text variant="bodyMd" tone="subdued">{brand.shopDomain}</Text>
                      <Badge tone={brand.isActive ? 'success' : 'critical'}>
                        {brand.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </InlineStack>
                  </BlockStack>
                </InlineStack>

                <InlineStack gap="300">
                  <Button
                    variant="tertiary"
                    icon={RefreshIcon}
                    onClick={fetchBrandData}
                    loading={loading}
                    disabled={isSyncing}
                  >
                    Refresh
                  </Button>
                  <Text variant="bodySm" tone="subdued">
                    Last updated: {(() => {
                      const date = new Date(activityLog[0]?.timestamp || brand.updatedAt);
                      return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
                    })()}
                  </Text>
                </InlineStack>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Configuration Tabs */}
        <Layout.Section>
          <Tabs
            tabs={tabs}
            selected={selectedTab}
            onSelect={isSyncing ? undefined : setSelectedTab}
          >
            <Box paddingBlockStart="600">
              {selectedTab === 0 && (
                <BlockStack gap="800">
                  <CommissionSettings
                    saving={saving}
                    setSaving={setSaving}
                    setError={setError}
                    isSyncing={isSyncing}
                    defaultRate={defaultRate}
                    setDefaultRate={setDefaultRate}
                    updateDefaultCommission={updateDefaultCommission}
                    brand={brand}
                    availableMarketplaceTags={availableMarketplaceTags}
                    availableMarketplacesLoading={availableMarketplacesLoading}
                    fetchBrandData={fetchBrandData}
                  />
                  <InvoiceSettings
                    newRecipientEmail={newRecipientEmail}
                    setNewRecipientEmail={setNewRecipientEmail}
                    brandId={brandId}
                    selectedTab={selectedTab}
                    apiClient={apiClient}
                  />
                </BlockStack>
              )}
              {selectedTab === 1 && renderShippingConfiguration()}
              {selectedTab === 2 && renderSyncTab()}
              {selectedTab === 3 && renderAnalytics()}
              {selectedTab === 4 && renderOrders()}
              {selectedTab === 5 && (
                <CommissionAdjustmentsTable
                  brandId={brandId}
                  showBrandColumn={false}
                  title={`${brand?.name || 'Brand'} - Commission Adjustments`}
                  subtitle="Manual commission adjustments for this brand"
                  embedded={true}
                />
              )}
              {selectedTab === 6 && renderActivityLog()}
            </Box>
          </Tabs>
        </Layout.Section>
      </Layout>

      {/* Return Fee Override Modal */}
      <Modal
        open={showReturnFeeModal}
        onClose={() => {
          setShowReturnFeeModal(false);
          setEditingMarketplace('');
          setNewReturnFee('0.00');
        }}
        title="Add Marketplace Return Fee Override"
        primaryAction={{
          content: 'Add Override',
          onAction: () => {
            if (editingMarketplace && !isNaN(parseFloat(newReturnFee))) {
              saveMarketplaceReturnFee(editingMarketplace, newReturnFee);
            }
          },
          loading: saving,
          disabled: !editingMarketplace || !newReturnFee.trim()
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => {
            setShowReturnFeeModal(false);
            setEditingMarketplace('');
            setNewReturnFee('0.00');
          }
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text tone="subdued" variant="bodyMd">
              {hasDefaultReturnFee
                ? `Override the default return fee ($${parseFloat(defaultReturnFee).toFixed(2)}) for a specific marketplace.`
                : 'Set a marketplace-specific return fee override. No default return fee is currently configured.'}
            </Text>
            <FormLayout>
              <Select
                label="Marketplace"
                options={[
                  { label: 'Select marketplace...', value: '' },
                  ...availableMarketplaceTags
                    .filter(marketplace => !returnFeeOverrideKeys.includes(marketplace))
                    .map(marketplace => ({
                      label: marketplace,
                      value: marketplace
                    }))
                ]}
                value={editingMarketplace}
                onChange={setEditingMarketplace}
                helpText={(() => {
                  if (marketplacesLoading) return "Loading available marketplaces...";

                  const unconfiguredMarketplaces = availableMarketplaceTags.filter(
                    marketplace => !returnFeeOverrideKeys.includes(marketplace)
                  );

                  if (unconfiguredMarketplaces.length > 0) {
                    return "Choose a marketplace for this return fee";
                  } else if (availableMarketplaceTags.length > 0) {
                    return "All available marketplaces already have brand-level return fees configured";
                  } else {
                    return "No marketplaces detected for this brand";
                  }
                })()}
                disabled={(() => {
                  if (marketplacesLoading) return true;

                  const unconfiguredMarketplaces = availableMarketplaceTags.filter(
                    marketplace => !returnFeeOverrideKeys.includes(marketplace)
                  );

                  return unconfiguredMarketplaces.length === 0;
                })()}
              />
              <TextField
                label="Return Fee"
                type="number"
                value={newReturnFee}
                onChange={setNewReturnFee}
                prefix="$"
                min="0"
                step="0.01"
                helpText="Fee charged for processing returns from this marketplace"
              />
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Reset Default Return Fee Modal */}
      <Modal
        open={showResetDefaultFeeModal}
        onClose={() => setShowResetDefaultFeeModal(false)}
        title="Reset Default Return Fee"
        primaryAction={{
          content: 'Reset to Default',
          destructive: true,
          loading: resettingFees,
          onAction: handleResetDefaultFee
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowResetDefaultFeeModal(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text>
              Are you sure you want to reset the default return fee to the global default?
            </Text>
            <Text tone="subdued">
              This will remove the brand-specific override and use the global default of{' '}
              <strong>${globalReturnFeeDefaults.default?.toFixed(2) || '0.00'}</strong>.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Reset All Marketplace Return Fees Modal */}
      <Modal
        open={showResetMarketplaceFeesModal}
        onClose={() => setShowResetMarketplaceFeesModal(false)}
        title="Reset All Marketplace Return Fees"
        primaryAction={{
          content: 'Reset All to Default',
          destructive: true,
          loading: resettingFees,
          onAction: handleResetMarketplaceFees
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowResetMarketplaceFeesModal(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text>
              Are you sure you want to reset all marketplace return fees to their global defaults?
            </Text>
            <Text tone="subdued">
              This will remove{' '}
              <strong>{returnFeeOverrideKeys.filter(key => key !== 'default').length}</strong>{' '}
              brand-specific marketplace override(s) and revert to global defaults.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}